import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { RegistryLink } from '@/components/RegistryLink';
import {
  Search, ArrowRight, Building2, Layers, Loader2, CheckCircle, XCircle,
  AlertTriangle, ChevronDown, ChevronUp, Package,
} from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const DECISION_STYLES = {
  APPROVE: { color: C.green, label: 'Approved' },
  CONDITIONS: { color: C.teal, label: 'Conditions' },
  REFER: { color: C.amber, label: 'Referred' },
  DECLINE: { color: C.red, label: 'Declined' },
};
const money = v => `$${Math.round(v || 0).toLocaleString('en-AU')}`;

function classDefaults(config) {
  const d = {};
  (config.submission_fields || []).forEach(f => { if (f.default !== undefined) d[f.name] = f.default; });
  const sc = config.section_config || [];
  if (sc.length) {
    d.sections_selected = sc.filter(s => s.default_on).map(s => s.id);
    sc.forEach(s => { if (s.default_on && s.amount_field) d[s.amount_field] = s.default_amount; });
  }
  return d;
}

const FieldGrid = ({ config, form, setForm }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {(config.submission_fields || []).map(field => (
      <div key={field.name}>
        <Label className="text-xs" style={{ color: C.slate }}>{field.label}</Label>
        {field.type === 'boolean' ? (
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs" style={{ color: C.slate }}>{field.label}</span>
            <Switch checked={!!form[field.name]} onCheckedChange={v => setForm(p => ({ ...p, [field.name]: v }))} />
          </div>
        ) : field.type === 'select' ? (
          <select value={form[field.name] || ''} onChange={e => setForm(p => ({ ...p, [field.name]: e.target.value }))}
            className="mt-1 w-full px-3 py-2 border border-input rounded-sm text-sm bg-white capitalize">
            <option value="">Select...</option>
            {(field.options || []).map(o => <option key={o} value={o}>{String(o).replace(/_/g, ' ')}</option>)}
          </select>
        ) : (
          <Input type={field.type === 'number' ? 'number' : 'text'} value={form[field.name] ?? ''}
            data-testid={`mc-field-${config._id}-${field.name}`}
            onChange={e => setForm(p => ({ ...p, [field.name]: field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
            className="mt-1 rounded-sm" placeholder={field.label} />
        )}
      </div>
    ))}
  </div>
);

export default function MultiClassSubmissionPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [classes, setClasses] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [entity, setEntity] = useState(null);
  const [appetite, setAppetite] = useState(null);
  const [selected, setSelected] = useState([]);       // ordered class ids
  const [configs, setConfigs] = useState({});          // class_id -> config
  const [forms, setForms] = useState({});              // class_id -> form data
  const [openForm, setOpenForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/insurance-classes').then(r => setClasses(Object.fromEntries(Object.entries(r.data).filter(([, v]) => v.state === 'active')))).catch(() => {}).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    if (!entity) { setAppetite(null); return; }
    api.get(`/classes/appetite?entity_id=${entity.entity_id}`).then(r => setAppetite(r.data)).catch(() => setAppetite(null));
  }, [entity, api]);

  const search = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(window._mcTimeout);
    window._mcTimeout = setTimeout(async () => {
      if (val.length < 2) { setResults([]); return; }
      try {
        const { data } = await api.get(`/entities/search?q=${encodeURIComponent(val)}&limit=8`);
        setResults(data);
      } catch (err) { console.error('search:', err?.message); }
    }, 300);
  };

  const toggleClass = async (classId) => {
    if (selected.includes(classId)) {
      setSelected(s => s.filter(c => c !== classId));
      return;
    }
    if (selected.length >= 6) return;
    let cfg = configs[classId];
    if (!cfg) {
      const { data } = await api.get(`/insurance-classes/${classId}`);
      cfg = { ...data, _id: classId };
      setConfigs(p => ({ ...p, [classId]: cfg }));
    }
    setForms(p => ({ ...p, [classId]: p[classId] || classDefaults(cfg) }));
    setSelected(s => [...s, classId]);
    setOpenForm(classId);
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const payload = { entity_id: entity.entity_id, classes: {} };
      selected.forEach(c => { payload.classes[c] = forms[c] || {}; });
      const { data } = await api.post('/submissions/multi-class', payload);
      setBundle(data);
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Package quote failed');
    }
    setSubmitting(false);
  };

  const reset = () => { setBundle(null); setSelected([]); setForms({}); setEntity(null); setQuery(''); setResults([]); setError(''); };

  if (loading) return <Skeleton className="h-64 rounded-sm" />;

  // ── Result: package summary ──
  if (bundle) {
    const quoted = bundle.classes.filter(r => !r.blocked);
    return (
      <div className="max-w-3xl mx-auto space-y-5" data-testid="package-result">
        <Card className="rounded-sm overflow-hidden border-0">
          <div className="p-5 text-white" style={{ background: 'linear-gradient(135deg, #0B1E3D 0%, #1A4C7C 100%)' }}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Package className="w-7 h-7" />
                <div>
                  <h2 className="text-lg font-bold">Package Quote — {bundle.business_name}</h2>
                  <p className="text-white/70 text-xs font-mono">{bundle.bundle_reference} · {bundle.classes_quoted} class(es) quoted{bundle.classes_blocked ? ` · ${bundle.classes_blocked} blocked` : ''}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-white/60">Total package premium</p>
                <p className="text-2xl font-bold font-mono" data-testid="package-premium">{money(bundle.package_premium)}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-sm bg-white p-4 space-y-2" data-testid="package-class-list">
          {bundle.classes.map(r => {
            if (r.blocked) {
              return (
                <div key={r.class_id} className="flex items-center gap-3 p-3 rounded-sm" style={{ background: 'rgba(226,75,74,0.05)' }} data-testid={`package-row-${r.class_id}`}>
                  <XCircle className="w-4 h-4 shrink-0" style={{ color: C.red }} />
                  <div className="flex-1">
                    <p className="text-sm font-medium" style={{ color: C.navy }}>{r.class_name}</p>
                    <p className="text-[11px]" style={{ color: C.red }}>{r.reason}</p>
                  </div>
                  <span className="text-xs font-semibold" style={{ color: C.red }}>NOT QUOTED</span>
                </div>
              );
            }
            const ds = DECISION_STYLES[r.decision] || DECISION_STYLES.REFER;
            return (
              <div key={r.class_id} className="flex items-center gap-3 p-3 rounded-sm border" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`package-row-${r.class_id}`}>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold shrink-0" style={{ background: ds.color + '18', color: ds.color }}>{ds.label.toUpperCase()}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: C.navy }}>{r.class_name}</p>
                  <p className="text-[11px]" style={{ color: C.slate }}>Score {r.composite_score?.toLocaleString()}/10,000{r.stp ? ' · STP' : ''}{r.status === 'bound' ? ' · POLICY BOUND' : ''}</p>
                </div>
                <span className="font-mono font-semibold text-sm shrink-0" style={{ color: C.navy }}>{money(r.total_payable)}</span>
                <Button size="sm" variant="outline" className="rounded-sm text-xs shrink-0" data-testid={`package-view-${r.class_id}`}
                  onClick={() => navigate(`/quotes/${r.submission_id}`)}>Review</Button>
              </div>
            );
          })}
        </Card>

        <p className="text-xs px-1" style={{ color: C.slate }}>
          Each class is a full submission on the pipeline — approve, bind or refine terms per class from its quote screen.
        </p>
        <Button onClick={reset} variant="outline" className="rounded-sm" data-testid="package-new-btn">New Package Quote</Button>
      </div>
    );
  }

  // ── Builder ──
  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="multi-class-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: 'rgba(0,180,216,0.12)' }}>
          <Layers className="w-5 h-5" style={{ color: C.teal }} />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight" style={{ color: C.navy }}>Multi-Class Package Quote</h1>
          <p className="text-sm mt-0.5" style={{ color: C.slate }}>Quote up to 6 insurance classes for one client in a single combined submission.</p>
        </div>
      </div>

      {/* Step 1: entity */}
      {!entity ? (
        <Card className="p-5 rounded-sm bg-white space-y-3" data-testid="mc-entity-search">
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>1 · Select the client</h3>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3" style={{ color: C.slate }} />
            <Input value={query} onChange={search} placeholder="Search by business name or ABN/NZBN..." className="pl-9 rounded-sm" data-testid="mc-search-input" />
          </div>
          {results.map(e => (
            <button key={e.entity_id} onClick={() => { setEntity(e); setResults([]); }} data-testid={`mc-entity-${e.entity_id}`}
              className="w-full flex items-center justify-between p-3 rounded-sm border hover:bg-[#F5F7FA] transition-colors" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
              <div className="text-left">
                <span className="text-sm font-medium" style={{ color: C.navy }}>{e.legal_name}</span>
                <span className="text-xs ml-2" style={{ color: C.slate }}>{e.city}, {e.state} | {e.abn || e.nzbn}</span>
              </div>
              <ArrowRight className="w-4 h-4" style={{ color: C.slate }} />
            </button>
          ))}
        </Card>
      ) : (
        <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: C.teal }}>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" style={{ color: C.blue }} />
            <span className="font-semibold text-sm" style={{ color: C.navy }}>{entity.legal_name}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-xs" style={{ color: C.slate }}>
            <span><RegistryLink abn={entity.abn} nzbn={entity.nzbn} prefix style={{ color: C.slate }} /></span>
            <span>{entity.city}, {entity.state}</span>
            <span>{entity.industry_code}</span>
          </div>
          <button type="button" onClick={() => { setEntity(null); setSelected([]); }} className="text-xs mt-1 hover:underline" style={{ color: C.blue }}>Change</button>
        </Card>
      )}

      {/* Step 2: class multi-select */}
      {entity && (
        <Card className="p-5 rounded-sm bg-white" data-testid="mc-class-picker">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.navy }}>2 · Pick the classes ({selected.length}/6)</h3>
          <p className="text-[11px] mb-3" style={{ color: C.slate }}>Appetite is checked live — blocked classes can't join the package.</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(classes).map(([id, c]) => {
              const app = appetite?.[id]?.status;
              // Out of appetite is a judgement; not quotable is a hard stop (no wording, no rating
              // table). Both are unselectable, and the reason says which it is.
              const notQuotable = c.quotable === false;
              const blocked = app === 'out_of_appetite' || notQuotable;
              const on = selected.includes(id);
              return (
                <button key={id} onClick={() => !blocked && toggleClass(id)} disabled={blocked} data-testid={`mc-class-${id}`}
                  className="px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: on ? C.blue : 'white', color: on ? 'white' : blocked ? C.red : C.navy,
                    borderColor: on ? C.blue : blocked ? 'rgba(226,75,74,0.4)' : app === 'refer' ? 'rgba(232,160,32,0.5)' : 'rgba(136,150,168,0.25)',
                  }}
                  title={notQuotable ? (c.blockers || [])[0]?.detail
                    : blocked ? appetite?.[id]?.reasons?.[0]
                      : app === 'refer' ? `Will refer: ${appetite?.[id]?.reasons?.[0]}` : ''}>
                  {c.name}{app === 'refer' && !on ? ' ⚠' : ''}{blocked ? ' ✕' : ''}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Step 3: per-class forms */}
      {entity && selected.length > 0 && (
        <div className="space-y-3" data-testid="mc-forms">
          <h3 className="text-xs font-semibold uppercase tracking-wider px-1" style={{ color: C.navy }}>3 · Risk details per class</h3>
          {selected.map(id => {
            const cfg = configs[id];
            const open = openForm === id;
            return (
              <Card key={id} className="rounded-sm bg-white overflow-hidden" data-testid={`mc-form-card-${id}`}>
                <button onClick={() => setOpenForm(open ? null : id)} className="w-full p-4 flex items-center justify-between hover:bg-[#F5F7FA]">
                  <span className="text-sm font-semibold" style={{ color: C.navy }}>{cfg?.name || id}</span>
                  <span className="flex items-center gap-2 text-xs" style={{ color: C.slate }}>
                    {appetite?.[id]?.status === 'refer' && <span style={{ color: '#B27516' }}><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />refers to UW</span>}
                    {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </span>
                </button>
                {open && cfg && (
                  <div className="px-4 pb-4">
                    <FieldGrid config={cfg} form={forms[id] || {}} setForm={fn => setForms(p => ({ ...p, [id]: fn(p[id] || {}) }))} />
                  </div>
                )}
              </Card>
            );
          })}

          {error && (
            <Card className="p-3.5 rounded-sm border-l-4 bg-white flex items-start gap-2.5" style={{ borderLeftColor: C.red }} data-testid="mc-error-banner">
              <XCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.red }} />
              <p className="text-xs" style={{ color: '#B03230' }}>{error}</p>
            </Card>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={submit} disabled={submitting || selected.length < 2} data-testid="mc-submit-btn"
              className="rounded-sm font-semibold px-8 text-white" style={{ background: C.blue, height: 44 }}>
              {submitting ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Scoring {selected.length} classes...</span>
                : <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Get Package Quote ({selected.length} classes)</span>}
            </Button>
            {selected.length < 2 && <span className="text-xs" style={{ color: C.slate }}>Select at least 2 classes</span>}
          </div>
        </div>
      )}
    </div>
  );
}
